import creditCardType from 'credit-card-type';
import { PaymentMethodSubtypeEnum } from '../types/payment.types';

// The package only exports its card-type shape from an internal module
// (credit-card-type/dist/types), so derive it from the function instead.
type CreditCardTypeModel = ReturnType<typeof creditCardType>[number];

/**
 * Maps credit-card-type library card types to our PaymentMethodSubtypeEnum
 */
const CARD_TYPE_MAPPING: Record<string, PaymentMethodSubtypeEnum> = {
	visa: PaymentMethodSubtypeEnum.CC_VISA,
	mastercard: PaymentMethodSubtypeEnum.CC_MASTERCARD,
	'american-express': PaymentMethodSubtypeEnum.CC_AMEX,
	discover: PaymentMethodSubtypeEnum.CC_DISCOVER,
	'diners-club': PaymentMethodSubtypeEnum.CC_DINERS_CLUB,
	jcb: PaymentMethodSubtypeEnum.CC_JCB,
};

/**
 * Sanitizes a card number by removing all non-digit characters.
 * Use this to clean user input before validation.
 * @param cardNumber - Raw card number input (may contain spaces, dashes, etc.)
 * @returns String containing only digits
 */
export function sanitizeCardNumber(cardNumber: string): string {
	if (!cardNumber) return '';
	return cardNumber.replace(/\D/g, '');
}

/**
 * Detects credit card subtype from card number
 * @param cardNumber - The credit card number (with or without formatting)
 * @returns PaymentMethodSubtypeEnum or undefined if card type cannot be determined
 */
export function detectCreditCardSubtype(
	cardNumber: string,
): PaymentMethodSubtypeEnum | undefined {
	const cleanCardNumber = sanitizeCardNumber(cardNumber);

	if (cleanCardNumber.length === 0) {
		return undefined;
	}

	// Use credit-card-type library to detect card type
	const detectedTypes: Array<CreditCardTypeModel> =
		creditCardType(cleanCardNumber);

	// Take the first (most likely) detected type
	const primaryType = detectedTypes[0];

	if (primaryType == null) {
		return undefined;
	}

	// Map to our enum
	const mappedType = CARD_TYPE_MAPPING[primaryType.type];

	if (!mappedType) {
		return undefined;
	}

	return mappedType;
}

/**
 * Validates credit card number format.
 * Strips all non-digits first, then validates the resulting number.
 * @param cardNumber - The credit card number to validate (may contain formatting)
 * @returns boolean indicating if the card number format is valid
 */
export function isValidCreditCardNumber(cardNumber: string): boolean {
	const cleanCardNumber = sanitizeCardNumber(cardNumber);

	if (cleanCardNumber.length === 0) {
		return false;
	}

	const detectedTypes: Array<CreditCardTypeModel> =
		creditCardType(cleanCardNumber);

	const primaryType = detectedTypes[0];

	if (primaryType == null) {
		return false;
	}

	// Check if the card number length matches expected lengths for the detected type
	return primaryType.lengths.includes(cleanCardNumber.length);
}
